import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, computed, inject, input } from "@angular/core";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import {
  MAX_PINNED_TOOLS,
  ToolPrefsService,
} from "@bitwarden/web-vault/app/gloss/plugins/tool-prefs.service";

export interface EditableTool {
  id: string;
  labelKey: string;
  /** Literal label (dev mock tiles); otherwise `labelKey` is translated. */
  label?: string;
  icon: string;
}

/**
 * Body of the "Edit your tools" right panel (opened in DialogWrapper). Pins up to
 * {@link MAX_PINNED_TOOLS} tools to keep them first; every change applies immediately via
 * {@link ToolPrefsService}, so the page behind the panel re-orders live.
 */
@Component({
  selector: "app-edit-tools-dialog",
  templateUrl: "./edit-tools-dialog.component.html",
})
export class EditToolsDialogComponent {
  readonly prefs = inject(ToolPrefsService);
  private readonly i18nService = inject(I18nService);
  readonly maxPins = MAX_PINNED_TOOLS;

  tools = input<EditableTool[]>([]);

  /** Screen-reader message after a keyboard move. */
  reorderAnnouncement = "";

  readonly pinned = computed(() => {
    const byId = new Map(this.tools().map((t) => [t.id, t]));
    return this.prefs
      .pins()
      .map((id) => byId.get(id))
      .filter((t): t is EditableTool => !!t);
  });

  readonly others = computed(() => {
    const pins = this.prefs.pins();
    return this.prefs.arrange(this.tools()).filter((t) => !pins.includes(t.id));
  });

  /** Counts every saved pin, including ones whose tool isn't shown, to match what `pin()` allows. */
  readonly spotsLeft = computed(() => this.maxPins - this.prefs.pins().length);

  /** Once the user has dragged tools into their own order, "most used first" no longer applies. */
  readonly hasManualOrder = computed(() => this.prefs.order().length > 0);

  onPinnedDrop(event: CdkDragDrop<EditableTool[]>): void {
    this.movePin(event.previousIndex, event.currentIndex);
  }

  /** Alt+Up / Alt+Down on a row's drag handle moves it one place. */
  onHandleKeydown(event: KeyboardEvent, index: number): void {
    if (!event.altKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) {
      return;
    }
    event.preventDefault();
    const target = index + (event.key === "ArrowUp" ? -1 : 1);
    const label = this.labelOf(this.pinned()[index]);
    const total = this.pinned().length;
    const moved = target >= 0 && target < total;
    if (moved) {
      this.movePin(index, target);
    }
    this.reorderAnnouncement = moved
      ? this.i18nService.t("pluginsToolMoved", label, (target + 1).toString(), total.toString())
      : this.i18nService.t("pluginsToolCannotMove", label);
    const handle = event.currentTarget as HTMLElement;
    // Re-rendering the list can drop focus; put it back on the moved row's handle.
    setTimeout(() => handle.focus());
  }

  /**
   * Reorders the shown pins. Pins whose tool isn't in the list (hidden or gone) keep their slot
   * instead of being dropped by the save.
   */
  private movePin(from: number, to: number): void {
    if (from === to) {
      return;
    }
    const shown = this.pinned().map((t) => t.id);
    moveItemInArray(shown, from, to);
    const isShown = new Set(shown);
    let next = 0;
    this.prefs.setPins(this.prefs.pins().map((id) => (isShown.has(id) ? shown[next++] : id)));
  }

  private labelOf(tool: EditableTool): string {
    return tool.label ?? this.i18nService.t(tool.labelKey);
  }
}
